import _ from 'underscore';

import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { Constants, AccountUtils } from '@bingads-webui-clientcenter/entity-utils';
import { FormUtils } from '@bingads-webui-clientcenter/common-utils';
import { set } from '@bingads-webui/cookie';

import { isInManagementTab } from '../helper/management-dashboard-helper';
import * as CommonUtils from '../common-utils';
import { gridsMap } from '../constants';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/accounts-summary');
const {
  bulkDeleteAccounts, bulkReactivateAccounts, bulkEditAbl, bulkEditManualHold,
} = globalContext.require('campaignui/feature/bulk-action-accounts-grid/index');
const { setLoadDirectlyLinkedAccounts } = globalContext.require('component/bingads-grid/service/linked-accounts-preferences');
const { getCCUrl: ccUrl } = globalContext.require('component/bingads-grid/shared/clientcenter-url-helper');
const globalConfig = globalContext.require('component/config/index');
const bulkTransferAccounts = globalContext.requireDefault('campaignui/feature/bulk-transfer-accounts/index');
const bulkUnlinkAccounts = globalContext.requireDefault('campaignui/feature/bulk-unlink-accounts/index');
const singleUnlinkAccounts = globalContext.requireDefault('campaignui/feature/single-unlink-accounts/index');
const { getLoadDirectlyLinkedAccountsPreference } = globalContext.require('component/bingads-grid/service/linked-accounts-preferences');
const jsdataCampaign = globalContext.require('component/js-data-campaign/index');

const { isManualHold, isDeleted: isAccountDeleted } = AccountUtils;
const { PaymentOption } = Constants;

const {
  navigate,
  currentCid,
  currentUid,
  isInImportTab,
  isInPerformanceTab,
  isInSmartPerformanceTab,
  isManagerAccount,
  getGridPermissions,
  currentCustomer,
} = CommonUtils;

const {
  IsCustomerHierarchyEnabled,
  IsManagementDashboardEnabled,
  IsOBOAgencyCreateAccountEnabled,
} = globalConfig.get('Permissions');

const BulkEditMenuItemIds = {
  PrimaryContactId: 'dropdown_bulkEdit_primary_contact',
  BusinessLocationId: 'dropdown_bulkEdit_business_location',
  ApplyHoldId: 'dropdown_bulkEdit_apply_hold',
  RemoveHoldId: 'dropdown_bulkEdit_remove_hold',
};

const AccountUrls = {
  AccountsBillingUrl: 'accounts',
  CreateAccountUrl: 'account/create',
  CreateAccountUrlOBO: 'customers/createcustomer',
  ExportAccountsUrl: 'accounts/RenderExcel',
  LinkaccountsUrl: 'agency/sendinvite',
};

const {
  AccountsBillingUrl,
  CreateAccountUrl,
  CreateAccountUrlOBO,
  ExportAccountsUrl,
  LinkaccountsUrl,
} = AccountUrls;

const {
  PrimaryContactId,
  BusinessLocationId,
  ApplyHoldId,
  RemoveHoldId,
} = BulkEditMenuItemIds;

const extractToolbarItems = toolbar => (toolbar.get() || {}).items;

const updateBulkEditMenuHandler = (toolbar, itemId, onClickHandler) => {
  const bulkEditConfig = _.findWhere(extractToolbarItems(toolbar), { id: 'dropdown_bulkEdit' });

  if (bulkEditConfig && bulkEditConfig.menu) {
    const itemConfig = _.findWhere(bulkEditConfig.menu.items, { id: itemId });

    itemConfig.onClick = onClickHandler;
    toolbar.update(bulkEditConfig);
  }
};

const setToolbarMenuEnable = (toolbar, id, enabled) => {
  const bulkEditConfig = _.findWhere(extractToolbarItems(toolbar), { id });

  if (bulkEditConfig && !bulkEditConfig.disabled !== enabled) {
    bulkEditConfig.disabled = !enabled;
    toolbar.update(bulkEditConfig);
  }
};

const setDropdownOptionEnabled = (toolbar, dropDownId, menuItemId, enabled) => {
  const dropDown = _.findWhere(extractToolbarItems(toolbar), { id: dropDownId });
  const bulkEditConfig = _.findWhere(dropDown.menu.items, { id: menuItemId });

  if (bulkEditConfig && bulkEditConfig.disabled !== !enabled) {
    bulkEditConfig.disabled = !enabled;
    bulkEditConfig.classes = enabled ? [] : ['disabled-dropdown-item'];
    toolbar.update(bulkEditConfig);
  }
};

// This function is duplicated in ui-next due to globalContext injection requirement to be used, and can be deleted when ui-next is GAed
const forceAccountsGridRefresh = ({
  gridView,
}) => {
  // To refresh the grid, we actually need to destroy the accounts from jsdata cache, so a new oData call get trigger to the server and new data get updated
  // gridView.refresh(true); needs to be call after data is invalidated otherwise the same set of data will be returned, without any network call
  // We also need to deselect the current grid selection to avoid unexpected behavior with selected items not updated in the model, even though the checkboxes are unselected correctly
  const invalidateAccountsData = jsdataCampaign.Account.destroyAll();
  invalidateAccountsData.then(() => {
    gridView.deselectAll();
    gridView.refresh(true);
  });
};

// Field "OwnerId" is assigned to the agency customer (special usage) or parent customer in current hierarchy when Hierarchy feature is on
// As temporary solution, use "ManagerAccounts" to determine if the account is linked or owned by current customer before Hierarchy feature GA.
const isAgencyOfAccount = (account, customerId) =>
  !_.isEmpty(account.ManagerAccounts) && _.any(account.ManagerAccounts, manager => manager.Id === customerId);

const transferAccounts = ({
  gridView,
}) => {
  const selectedAccounts = gridView.selectedItems();

  bulkTransferAccounts({ selectedAccounts, customerId: currentCid(), userId: currentUid() });
};

const unlinkAccounts = ({
  gridView,
}) => {
  const selectedAccounts = gridView.selectedItems();

  if (IsCustomerHierarchyEnabled) {
    bulkUnlinkAccounts({
      selectedAccounts,
      customerId: currentCid(),
      userId: currentUid(),
      onConfirm: (revisedModel, redirectUri) => {
        FormUtils.postRedirect({
          url: redirectUri,
          fields: [
            {
              name: 'unlinkacc',
              value: JSON.stringify(revisedModel),
            },
          ],
        });
      },
    });
  } else {
    singleUnlinkAccounts({
      currentCid: currentCid(), userId: currentUid(), managedAccount: selectedAccounts[0], managingCustomer: currentCustomer(),
    });
  }
};

const bulkABL = ({
  gridView,
}) => {
  const selectedAccounts = gridView.selectedItems();
  const cid = currentCid();
  const uid = currentUid();

  bulkEditAbl({
    selectedAccounts, customerId: cid, userId: uid,
  });
};

const bulkManualHold = ({
  gridView,
  isApplyHold,
}) => {
  const selectedAccounts = gridView.selectedItems();

  bulkEditManualHold({
    selectedAccounts, isApplyHold, successCallback: () => forceAccountsGridRefresh({ gridView }),
  });
};

const updateToolbarMenuHandler = ({
  view: {
    gridView,
    primaryContactBulkEditor,
    toolbarFacade: { currentToolbar },
  },
}) => {
  primaryContactBulkEditor.registerToolbar(currentToolbar);
  updateBulkEditMenuHandler(currentToolbar, PrimaryContactId, () => primaryContactBulkEditor.show({
    cid: currentCid(),
    uid: currentUid(),
    callBack: () => forceAccountsGridRefresh({ gridView }),
  }));
  updateBulkEditMenuHandler(currentToolbar, BusinessLocationId, () => bulkABL({ gridView }));

  updateBulkEditMenuHandler(currentToolbar, ApplyHoldId, () => bulkManualHold({ gridView, isApplyHold: true }));
  updateBulkEditMenuHandler(currentToolbar, RemoveHoldId, () => bulkManualHold({ gridView, isApplyHold: false }));
};

const updateToolbarCustomerHierarchySelector = ({
  currentToolbar,
  gridName,
}) => {
  getLoadDirectlyLinkedAccountsPreference(gridName).then(({ loadDirectlyLinkedAccounts }) => {
    const update = {
      id: 'accountsSelection_selector',
      selectedKey: loadDirectlyLinkedAccounts ? 'accountsSelection_showLinkedAccounts' : 'accountsSelection_showAllAccounts',
    };
    currentToolbar.update(update);
  });
};

const customerHierarchySelector = {
  type: 'selector',
  id: 'accountsSelection_selector',
  selectedKey: 'accountsSelection_showLinkedAccounts',
  items: [{
    text: i18n.getString('AccountsSelection_showAllAccounts'),
    key: 'accountsSelection_showAllAccounts',
  }, {
    text: i18n.getString('AccountsSelection_showDirectlyLinkedAccounts'),
    key: 'accountsSelection_showLinkedAccounts',
  }],
};

const importGridSelector = {
  type: 'text-view',
  text: i18n.getString('AccountsSelection_ShowingDirectlyLinkedAccountsOnly'),
};

const createAccountButton = {
  type: 'button',
  id: 'create-account',
  text: i18n.getString('Menu_CreateAccount'),
  classes: ['btn', 'btn-hero'],
  onClick: () => {
    const createAccountOBOUrl = ccUrl(CreateAccountUrlOBO, { cid: currentCid(), rurl: window.location.href });
    const creationUrl = IsManagementDashboardEnabled ?
      ccUrl(CreateAccountUrl, { cid: currentCid(), uid: currentUid() }) :
      ccUrl(AccountsBillingUrl, { cid: currentCid(), uid: currentUid(), ecaf: true });
    const url = IsOBOAgencyCreateAccountEnabled ? createAccountOBOUrl : creationUrl;
    navigate(url);
  },
};

const deleteAccountButton = {
  type: 'button',
  id: 'delete-account',
  text: i18n.getString('Menu_DeleteAccount'),
  classes: ['btn', 'btn-secondary'],
  disabled: true,
};

const reactivateAccountButton = {
  type: 'button',
  id: 'reactivate-account',
  text: i18n.getString('Menu_ReactivateAccount'),
  classes: ['btn', 'btn-secondary'],
  disabled: true,
};

const linkAccountButton = {
  type: 'button',
  id: 'link-account',
  text: i18n.getString('Menu_LinkAccount'),
  classes: ['btn', 'btn-secondary'],
  onClick: () => navigate(ccUrl(LinkaccountsUrl, { cid: currentCid(), uid: currentUid() })),
};

const unlinkButton = {
  type: 'button',
  id: 'unlink-account',
  text: i18n.getString('Menu_UnlinkAccount'),
  classes: ['btn', 'btn-secondary'],
  disabled: true,
};

const transferButton = {
  type: 'button',
  id: 'transfer-account',
  text: i18n.getString('Menu_TransferAccount'),
  classes: ['btn', 'btn-secondary'],
  disabled: true,
};

const bulkEditDropdown = {
  type: 'dropdown',
  id: 'dropdown_bulkEdit',
  button: {
    text: i18n.getString('GridToolbar_FilterBar_Edit'),
  },
  classes: ['btn-secondary'],
  disabled: true,
  menu: {
    items: [{
      text: i18n.getString('ColumnName_PrimaryContact'),
      id: PrimaryContactId,
    }, {
      text: i18n.getString('ColumnName_BusinessLocation'),
      id: BusinessLocationId,
    }],
  },
};

const bulkManualHoldMenuItems = [{
  classes: ['divider'],
}, {
  text: i18n.getString('BulkEditDropDownName_ApplyHold'),
  id: ApplyHoldId,
}, {
  text: i18n.getString('BulkEditDropDownName_RemoveHold'),
  id: RemoveHoldId,
}];

const exportButton = {
  type: 'button',
  id: 'export-account',
  text: i18n.getString('Grid_ExportButtonText'),
  classes: ['btn', 'btn-secondary'],
  onClick: () => navigate(ccUrl(ExportAccountsUrl, { cid: currentCid(), uid: currentUid() })),
};

const editDropdown = {
  type: 'dropdown',
  id: 'dropdown-edit',
};
const downloadButton = {
  type: 'button',
  id: 'button-download',
};
const segmentDropdown = {
  type: 'dropdown',
  id: 'dropdown-segment',
  button: {
    text: 'Segment',
  },
};
const columnChooserDropdown = {
  type: 'dropdown',
  id: 'dropdown-column-chooser',
};
const dropdownFilter = {
  type: 'dropdown',
  id: 'dropdown-filter',
};
const searchBoxFilter = {
  type: 'search-box',
  id: 'search-box-filter',
};
const importFromGoogleAdsButton = {
  type: 'button',
  id: 'import-from-googleads',
  text: i18n.getString('Import_GoogleSource_Rebrand'),
  classes: ['btn', 'btn-hero'],
};

export const getToolbarItems = ({
  gridType,
  isForVNext,
}) => {
  let toolbarItems = [];

  const gridPermissions = getGridPermissions({ gridType });
  const isImportGrid = isInImportTab(gridType);
  const isPerformanceGrid = isInPerformanceTab(gridType);
  const isSmartPerformanceGrid = isInSmartPerformanceTab(gridType);


  if (_.every(bulkEditDropdown.menu.items, s => s.id !== ApplyHoldId)) {
    bulkEditDropdown.menu.items = [
      ...bulkEditDropdown.menu.items,
      ...bulkManualHoldMenuItems,
    ];
  }

  toolbarItems = _.union(toolbarItems, _.compact([
    gridPermissions.createAccount && createAccountButton,
    gridPermissions.checkAndDeleteAccount && deleteAccountButton,
    gridPermissions.checkAndReactivateAccount && reactivateAccountButton,
    gridPermissions.linkAccount && linkAccountButton,
    gridPermissions.checkAndUnlinkAccount && unlinkButton,
    gridPermissions.checkAndTransferAccount && isForVNext && transferButton,
    gridPermissions.checkAndEditAccount && bulkEditDropdown,
    gridPermissions.exportAccount && exportButton,
  ]));

  if (isPerformanceGrid || isSmartPerformanceGrid) {
    // performance tab
    toolbarItems = _.union(toolbarItems, [editDropdown, downloadButton, columnChooserDropdown, segmentDropdown, dropdownFilter, searchBoxFilter]);
  } else if (isImportGrid) {
    toolbarItems = _.union([importFromGoogleAdsButton], [dropdownFilter, searchBoxFilter]);
  } else {
    // management tab
    toolbarItems = _.union(toolbarItems, [editDropdown, columnChooserDropdown, dropdownFilter, searchBoxFilter]);
  }

  if (IsCustomerHierarchyEnabled) {
    toolbarItems = _.union(toolbarItems, [isImportGrid ? importGridSelector : customerHierarchySelector]);
  }

  return toolbarItems;
};

// Enable/disable the toolbar menu based on selection
export const updateToolbarAfterSelection = ({
  view: {
    gridView,
    primaryContactBulkEditor,
    toolbarFacade: { currentToolbar },
  },
  gridType,
}) => {
  const selectedAccounts = gridView.selectedItems();
  const hasSelection = selectedAccounts.length > 0;

  if (IsCustomerHierarchyEnabled) {
    setToolbarMenuEnable(currentToolbar, unlinkButton.id, hasSelection);
  } else if (IsManagementDashboardEnabled) {
    const singleAccountSelection = selectedAccounts.length === 1;
    const singleLinkedAccountSelected = singleAccountSelection && isAgencyOfAccount(selectedAccounts[0], currentCid());

    setToolbarMenuEnable(currentToolbar, unlinkButton.id, singleLinkedAccountSelected);
  }

  if (isInManagementTab(gridType)) {
    const onlyAdAccounts = !_.some(selectedAccounts, account => isManagerAccount(account));
    const onlyDeletedAccountSelected = hasSelection && _.every(selectedAccounts, account => isAccountDeleted(account));
    const noDeletedAccountSelected = hasSelection && !_.some(selectedAccounts, account => isAccountDeleted(account));
    const bulkEditDropdownEnabled = onlyAdAccounts && noDeletedAccountSelected;

    setToolbarMenuEnable(currentToolbar, bulkEditDropdown.id, bulkEditDropdownEnabled);
    setToolbarMenuEnable(currentToolbar, deleteAccountButton.id, onlyAdAccounts && noDeletedAccountSelected);
    setToolbarMenuEnable(currentToolbar, reactivateAccountButton.id, onlyAdAccounts && onlyDeletedAccountSelected);

    // Update manual hold option
    if (bulkEditDropdownEnabled) {
      const isAllInvoiceAccount = hasSelection && _.every(selectedAccounts, account => _.isObject(account) && PaymentOption.Invoice === account.PaymentOption);
      const onlyUnManualHoldAccountSelected = isAllInvoiceAccount && _.every(selectedAccounts, account => !isManualHold(account));
      const onlyManualHoldAccountSelected = isAllInvoiceAccount && _.every(selectedAccounts, account => isManualHold(account));

      setDropdownOptionEnabled(currentToolbar, bulkEditDropdown.id, ApplyHoldId, onlyUnManualHoldAccountSelected);
      setDropdownOptionEnabled(currentToolbar, bulkEditDropdown.id, RemoveHoldId, onlyManualHoldAccountSelected);
    }

    // Update Primary Contact plugin when selected accounts changes on the grid.
    if (primaryContactBulkEditor.isInEditing()) {
      primaryContactBulkEditor.show(currentCid(), currentUid());
    }
  }
};

export const updateToolbarAfterGridInitialized = ({
  view,
  view: {
    gridView,
    toolbarFacade: { currentToolbar, updateButtonConfig },
  },
  gridType,
}) => {
  const { gridName } = gridsMap[gridType];

  if (IsCustomerHierarchyEnabled) {
    updateToolbarCustomerHierarchySelector({ currentToolbar, gridName });
  }

  updateButtonConfig({
    itemId: deleteAccountButton.id,
    toolbarConfig: {
      onClick: () => {
        const selectedAccounts = gridView.selectedItems();
        bulkDeleteAccounts({ selectedAccounts, successCallback: () => forceAccountsGridRefresh({ gridView }) });
      },
    },
  });

  updateButtonConfig({
    itemId: customerHierarchySelector.id,
    toolbarConfig: {
      onChange: (item) => {
        setLoadDirectlyLinkedAccounts(gridName, item.key === 'accountsSelection_showAllAccounts' ? 'false' : 'true').then(() => {
          gridView.refresh(true);
        });
      },
    },
  });

  updateButtonConfig({
    itemId: reactivateAccountButton.id,
    toolbarConfig: {
      onClick: () => {
        const selectedAccounts = gridView.selectedItems();
        bulkReactivateAccounts({ selectedAccounts, successCallback: () => forceAccountsGridRefresh({ gridView }) });
      },
    },
  });

  updateButtonConfig({
    itemId: unlinkButton.id,
    toolbarConfig: {
      onClick: () => unlinkAccounts({ gridView }),
    },
  });

  updateButtonConfig({
    itemId: transferButton.id,
    toolbarConfig: {
      onClick: () => transferAccounts({ gridView }),
    },
  });

  if (isInManagementTab(gridType)) {
    // TODO: Task 2178697: Bulk primary contact top-of-grid form
    // TODO: Task 2194573: Migrate the bulk edit logic
    updateToolbarMenuHandler({ view });
  }

  if (isInImportTab(gridType)) {
    updateButtonConfig({
      itemId: importFromGoogleAdsButton.id,
      toolbarConfig: {
        onClick: () => {
          const selectedAccounts = gridView.selectedKeys();
          // reserve task item id in cookie
          set('multi-account-presseleced-account-ids', selectedAccounts.join(','));
          navigate(`/Campaign/AccountsSummary?cid=${currentCid()}&uid=${currentUid()}#customer/${currentCid()}/import-accounts/googleImport`);
        },
      },
    });
  }
};

